import React, { useRef, useCallback, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";

export interface InfiniteScrollListProps {
  /** Whether more items are available to load. */
  hasMore: boolean;
  /** Whether a fetch is in progress. */
  isLoading: boolean;
  /** Called when the user scrolls near the bottom. */
  onLoadMore: () => void;
  /** Distance from the bottom (px) at which to trigger loading. @default 100 */
  threshold?: number;
  /** Content to render inside the scrollable area. */
  children: React.ReactNode;
  /** Optional sx overrides for the scroll container. */
  sx?: Record<string, unknown>;
}

/**
 * A generic scrollable container that calls `onLoadMore` when the user
 * scrolls near the bottom. Drop any list / table inside as `children`.
 *
 * Usage:
 * ```tsx
 * <InfiniteScrollList hasMore={hasMore} isLoading={isLoading} onLoadMore={loadMore}>
 *   <Table>…</Table>
 * </InfiniteScrollList>
 * ```
 */
export default function InfiniteScrollList({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 100,
  children,
  sx,
}: InfiniteScrollListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || isLoading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight <= threshold) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore, threshold]);

  // Attach scroll listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowY: "auto",
        flex: 1,
        ...sx,
      }}
    >
      {children}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}
